import config from 'config';
import { List, PaginatedResult, PaginationOptions, SortOptions } from 'types/api';
import {
  CompanyListItem,
  CompanyProfile,
  CompanyStats,
  ExecutiveSummaryDTO,
  ICompany,
  UserStats,
  VideoListDTO,
} from 'types/company/company';
import { GreenlistOrganizationDTO } from 'types/organization';
import {
  BackofficeEvent,
  BackofficeEventListItem,
  BackofficeMatchmakingCompanySummaryDTO,
  BackofficeMatchmakingCompanyMatchDTO,
  BackofficeMatchmakingUserMatchDTO,
  BackofficeMatchmakingUserSummaryDTO,
  BackofficeOrganizationDTO,
  BackofficePatchCommunity,
  BackofficeUpdateCompanyDTO,
  BackofficeUserDTO,
} from 'types/backoffice';
import { UserProfile } from 'types/user';
import { CompactEventDTO } from 'ui/domain/Event/types';
import ApiBase from '../ApiBase';
import { withPagination } from 'apis/utils';
import { CompanyId } from 'urls';
import { CloudinaryUploadSettings, ImpactGoal, Industry } from 'types';
import { MarketPotential } from 'apis/CompanyAPI/companies/useMarketPotential';
import { HighlightedSectionList } from 'apis/CompanyAPI/companies/companiesApi';
import { SortDirection } from 'ui/elements/tables/Table/useSorting';
import { TinyCommunity } from 'types/company/community';
import { PulseItemDTO } from 'apis/DashboardAPI';
import { ContentStats, UpdateStats } from 'types/content';

export interface SendGridEventDTO {
  id: UUID;
  companyId?: number;
  emailType?: string;
  timestamp?: number;
  createdAt?: string;
}

export interface BackofficeSendGridEventsListDTO {
  values: SendGridEventDTO[];
  pageSize: number;
  limit: number;
  total: number;
  page: number;
  pages: number;
}

const baseUrl = config.COMPANIES_API_URL;

const api = ApiBase();

export const backofficeApiUrls = {
  companies: {
    list: (
      paginationOptions: PaginationOptions,
      options: {
        showApproved?: boolean;
        showTestCompanies?: boolean;
        hasCompletedBasicProfile?: boolean;
        name?: string;
        sortBy?: string;
        sortOrder?: SortDirection;
        hasAdmins?: boolean;
        adminsBouncing?: boolean;
      },
    ) => {
      return withPagination(`${baseUrl}/backoffice/companies`, paginationOptions, options);
    },
    get: (companyId: CompanyId) => `${baseUrl}/backoffice/companies/${companyId}`,
    pulse: (companyId: CompanyId, paginationOptions?: PaginationOptions) =>
      withPagination(`${baseUrl}/backoffice/companies/${companyId}/pulse`, paginationOptions),
    sendgridEvents: (companyId: CompanyId, paginationOptions?: PaginationOptions) =>
      withPagination(`${config.NOTIFY_API_URL}/backoffice/sendgrid-events/company/${companyId}`, paginationOptions),
  },
  communities: {
    list: `${baseUrl}/backoffice/communities`,
    application: {
      getConfiuration: (communityId: number) => `${baseUrl}/backoffice/communities/${communityId}/application`,
      putConfiguration: (communityId: number) => `${baseUrl}/backoffice/communities/${communityId}/application`,
      post: (communityId: number) => `${baseUrl}/backoffice/communities/${communityId}/application/questions`,
      patch: (communityId: number, questionId: UUID) =>
        `${baseUrl}/backoffice/communities/${communityId}/application/questions/${questionId}`,
      delete: (communityId: number, questionId: UUID) =>
        `${baseUrl}/backoffice/communities/${communityId}/application/questions/${questionId}`,
    },
  },
  organizations: {
    get: (organizationId: UUID) => `${baseUrl}/backoffice/organizations/${organizationId}`,
    list: (
      paginationOptions: PaginationOptions,
      options: {
        showApproved?: boolean;
        isTestOrganization?: boolean;
        name?: string;
        sortBy?: string;
        sortOrder?: SortDirection;
        organizationTypes?: string[];
      },
    ) => {
      return withPagination(`${baseUrl}/backoffice/organizations`, paginationOptions, options);
    },
    patch: (organizationId: UUID) => `${baseUrl}/backoffice/organizations/${organizationId}`,
    patchSettings: (organizationId: UUID) => `${baseUrl}/backoffice/organizations/${organizationId}/settings`,
  },
  images: {
    getUploadUrl: (folder: string, filename: string) => `${baseUrl}/backoffice/images/${folder}?filename=${filename}`,
  },
};

export default {
  stats: {
    userStats: () => api.get<UserStats>(`${baseUrl}/stats/user`),
    companyStats: () => api.get<CompanyStats>(`${baseUrl}/stats/company`),
    globalStats: () => api.get<ContentStats>(`${baseUrl}/backoffice/updates/stats`),
    updateStats: (updateId: UUID) => api.get<UpdateStats>(`${baseUrl}/backoffice/updates/stats/${updateId}`),
  },
  companies: {
    patch: (companyId: CompanyId, dto: BackofficeUpdateCompanyDTO) =>
      api.patch<ICompany>(`${baseUrl}/backoffice/companies/${companyId}`, dto),
    delete: (companyId: CompanyId) => api.deleteRequest(`${baseUrl}/backoffice/companies/${companyId}`),
    getProfile: (id: CompanyId) => api.get<CompanyProfile>(`${baseUrl}/backoffice/companies/${id}/profile`),
    joinCommunity: (companyId: number, inviteCode: string) =>
      api.post<void>(`${baseUrl}/backoffice/companies/${companyId}/join-community/${inviteCode}`, {}),
    getProfileSections: (id: CompanyId) =>
      api.get<{
        videos: VideoListDTO;
        marketPotential?: MarketPotential;
        executiveSummary: ExecutiveSummaryDTO;
        impactgoals: ImpactGoal[];
        team: HighlightedSectionList;
      }>(`${baseUrl}/backoffice/companies/${id}/profile-sections`),
    getPulse: (companyId: CompanyId, paginationOptions?: PaginationOptions) =>
      api.get<PaginatedResult<PulseItemDTO>>(`${baseUrl}/backoffice/companies/${companyId}/pulse`, {
        requestParams: paginationOptions,
      }),
    getSendGridEvents: (companyId: CompanyId, paginationOptions?: PaginationOptions) =>
      api.get<BackofficeSendGridEventsListDTO>(
        `${config.NOTIFY_API_URL}/backoffice/sendgrid-events/company/${companyId}`,
        { requestParams: paginationOptions },
      ),
  },
  communities: {
    patch: (communityId: number, dto: BackofficePatchCommunity) =>
      api.patch(`${baseUrl}/backoffice/communities/${communityId}`, dto),
    patchLinkedCommunities: (communityId: number, dto: { communityIds: number[] }) =>
      api.patch(`${baseUrl}/backoffice/communities/${communityId}/linked-communities`, dto),
    getLinkedCommunities: (communityId: number) =>
      api.get<List<TinyCommunity>>(`${baseUrl}/backoffice/communities/${communityId}/linked-communities`),
    listTeam: (communitySlug: string, params?: PaginationOptions) =>
      api.get<PaginatedResult<UserProfile>>(`${baseUrl}/backoffice/communities/${communitySlug}/team`, {
        requestParams: params,
      }),
  },
  users: {
    queryUsers: (
      filter: { name?: string; email?: string },
      paginationOptions: PaginationOptions,
      sortOrder?: SortOptions,
    ) =>
      api.get<PaginatedResult<BackofficeUserDTO>>(`${baseUrl}/backoffice/users`, {
        requestParams: { ...paginationOptions, ...filter, ...sortOrder },
      }),
    get: (id: number) => `${baseUrl}/backoffice/users/${id}`,
    delete: (email: string) => api.deleteRequest(`${baseUrl}/backoffice/users`, { email }),
    deletionRequests: {
      post: (cwUserid: string) => api.post(`${baseUrl}/backoffice/users/delete-requests/${cwUserid}`, {}),
      get: `${baseUrl}/backoffice/users/delete-requests`,
    },
  },
  notifyEvents: {
    list: (
      eventType: string[],
      hasError: string | undefined,
      hasWorkFailures: string | undefined,
      paginationOptions: PaginationOptions,
    ) => {
      const params = new URLSearchParams();
      eventType.map(type => params.append('eventType', type));
      if (hasError) {
        params.append('hasError', '1');
      }
      if (hasWorkFailures) {
        params.append('hasWorkFailures', '1');
      }

      return api.get<PaginatedResult<BackofficeEventListItem>>(
        `${config.NOTIFY_API_URL}/backoffice/mail-events?${params.toString()}`,
        { requestParams: { ...eventType, ...paginationOptions } },
      );
    },
    get: (id: number, workStatusFilter: string[], eventTypeFilter: string[]) => {
      const params = new URLSearchParams();
      workStatusFilter.map(value => params.append('workStatus', value));
      eventTypeFilter.map(value => params.append('eventType', value));

      return api.get<BackofficeEvent>(
        `${config.NOTIFY_API_URL}/backoffice/mail-events/event/${id}?${params.toString()}`,
      );
    },
    retry: (id: number) => api.post(`${config.NOTIFY_API_URL}/backoffice/mail-events/event/${id}/retry`, {}),
  },
  events: {
    list: (paginationOptions?: PaginationOptions, filter?: { title?: string }) =>
      api.get<PaginatedResult<CompactEventDTO>>(
        withPagination(`${baseUrl}/backoffice/events`, paginationOptions, filter),
      ),
  },
  organizations: {
    list: (filters: { name?: string }) =>
      api.get<GreenlistOrganizationDTO[]>(`${baseUrl}/backoffice/organizations`, { requestParams: filters }),
    get: (organizationId: string) =>
      api.get<BackofficeOrganizationDTO>(`${baseUrl}/backoffice/organizations/${organizationId}`),
    queryCommunities: (query: string) =>
      api.get<CompanyListItem[]>(`${baseUrl}/backoffice/organizations/communities?query=${query}`),
    listOrganizationCommunities: (organizationId: string) =>
      api.get<TinyCommunity[]>(`${baseUrl}/backoffice/organizations/${organizationId}/communities`),
    create: (payload: any) => api.post<{ id: UUID }>(`${baseUrl}/backoffice/organizations`, payload),
    update: (organizationId: string, payload: any) =>
      api.put(`${baseUrl}/backoffice/organizations/${organizationId}`, payload),
    approve: (organizationId: UUID, payload: { isPlatformApproved: boolean }) =>
      api.patch(`${baseUrl}/backoffice/organizations/${organizationId}/approve`, payload),
    delete: (organizationId: string) => api.deleteRequest(`${baseUrl}/backoffice/organizations/${organizationId}`),
  },
  matchmaking: {
    users: (
      paginationOptions?: PaginationOptions,
      filters?: { name?: string; investorType?: string[] },
      sortOptions?: SortOptions,
    ) =>
      api.get<PaginatedResult<BackofficeMatchmakingUserSummaryDTO>>(`${baseUrl}/backoffice/matchmaking/users`, {
        requestParams: { ...paginationOptions, ...filters, ...sortOptions },
      }),
    companies: (
      paginationOptions?: PaginationOptions,
      filters?: { name?: string; isPlatformApproved?: boolean; hasActiveFundingRound?: boolean },
      sortOptions?: SortOptions,
    ) =>
      api.get<PaginatedResult<BackofficeMatchmakingCompanySummaryDTO>>(`${baseUrl}/backoffice/matchmaking/companies`, {
        requestParams: { ...paginationOptions, ...filters, ...sortOptions },
      }),
    getMatchesForUser: (cwUserId: string) =>
      api.get<BackofficeMatchmakingUserMatchDTO[]>(`${baseUrl}/backoffice/matchmaking/users/${cwUserId}`),
    getMatchesForCompany: (
      companyId: string,
      paginationOptions?: PaginationOptions,
      filters?: { userName?: string; investorType?: string; state?: string; matchReason?: string },
      sortOptions?: SortOptions,
    ) =>
      api.get<PaginatedResult<BackofficeMatchmakingCompanyMatchDTO>>(
        `${baseUrl}/backoffice/matchmaking/companies/${companyId}`,
        { requestParams: { ...paginationOptions, ...filters, ...sortOptions } },
      ),
  },
  industries: {
    list: () =>
      api.get<List<{ industry: Industry; numCompanies: number; numInvestors: number }>>(
        `${baseUrl}/backoffice/industries`,
      ),
    patch: (industryId: number, tags: string[]) =>
      api.patch(`${baseUrl}/backoffice/industries/${industryId}`, { tags }),
  },
  registrationTracking: {
    list: (
      paginationOptions?: PaginationOptions,
      filter?: {
        days?: number;
        utmSource?: string;
        utmMedium?: string;
        utmCampaign?: string;
        utmTerm?: string;
        utmContent?: string;
        sortBy?: string;
        sortOrder?: SortDirection;
      },
    ) => withPagination(`${baseUrl}/backoffice/registration-tracking`, paginationOptions, filter),
    users: (queryParams: string, dayInterval?: number) =>
      `${baseUrl}/backoffice/registration-tracking/users${queryParams}&days=${dayInterval}`,
  },
  images: {
    getUploadUrl: (folder: string, filename: string) =>
      api.get<CloudinaryUploadSettings>(backofficeApiUrls.images.getUploadUrl(folder, filename)),
  },
};
